/**
 * IP Restriction Guard
 *
 * Monitors for IP address changes and blocks access if the user's IP
 * is no longer whitelisted (for Enterprise orgs with IP restrictions enabled).
 *
 * This guards against:
 * - VPN disconnects mid-session
 * - IP address changes (mobile networks, etc.)
 * - Man-in-the-middle attacks that might change the apparent IP
 *
 * Checks happen on:
 * - Route navigation (path changes)
 * - Window focus (returning to tab after being away)
 * - NOT polling - the API already blocks requests, so we catch issues naturally
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { Modal } from "@/components/ui/Modal";
import { AlertTriangle, Shield, RefreshCw } from "lucide-react";

// Minimum time between IP checks (prevents rapid-fire checks on fast navigation)
const MIN_CHECK_INTERVAL_MS = 5000; // 5 seconds

export function IpRestrictionGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isAuthenticated, organization, logout } = useAuthStore();
  const [blocked, setBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState<string>("");
  const [currentIp, setCurrentIp] = useState<string>("");
  const [checking, setChecking] = useState(false);
  const lastCheckRef = useRef<number>(0);
  const lastPathRef = useRef<string>("");

  // Only check for Enterprise orgs
  const isEnterprise =
    organization?.billing_tier === "enterprise" ||
    organization?.billing_tier === "enterprise_plus";

  const checkIpStatus = useCallback(
    async (force = false) => {
      // Don't check if not authenticated or not enterprise
      if (!isAuthenticated || !isEnterprise) {
        return;
      }

      // Rate limit checks unless forced
      const now = Date.now();
      if (!force && now - lastCheckRef.current < MIN_CHECK_INTERVAL_MS) {
        return;
      }

      lastCheckRef.current = now;

      try {
        const status = await api.getIpWhitelistStatus();
        setCurrentIp(status.currentIp);
        setBlocked(false);
      } catch (err: any) {
        // Check if this is an IP restriction error
        if (err.status === 403 && err.data?.code === "IP_RESTRICTED") {
          setBlocked(true);
          setBlockReason(err.data?.error || "Access denied from this IP address");
          setCurrentIp(err.data?.currentIp || "Unknown");
        }
        // Other errors are ignored - could be network issues
      }
    },
    [isAuthenticated, isEnterprise]
  );

  // Check on route navigation (only when path actually changes)
  useEffect(() => {
    if (isAuthenticated && isEnterprise && location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      checkIpStatus();
    }
  }, [location.pathname, isAuthenticated, isEnterprise, checkIpStatus]);

  // Check on window focus (user returning to tab after being away)
  useEffect(() => {
    if (!isAuthenticated || !isEnterprise) {
      return;
    }

    const handleFocus = () => {
      // Force check when user returns to tab - they may have disconnected VPN
      checkIpStatus(true);
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isAuthenticated, isEnterprise, checkIpStatus]);

  // Handle retry
  const handleRetry = async () => {
    setChecking(true);
    await checkIpStatus(true);
    setChecking(false);
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  // If blocked, show the block screen
  if (blocked) {
    return (
      <Modal open onOpenChange={() => {}}>
        <Modal.Content size="md" hideCloseButton className="rounded-2xl shadow-2xl">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h1>

            <p className="text-slate-600 mb-6">{blockReason}</p>

            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-sm">
                <span className="text-slate-500">Your IP address:</span>
                <code className="bg-slate-200 px-2 py-1 rounded font-mono text-slate-900">
                  {currentIp}
                </code>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3 text-left">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Your IP is not whitelisted</p>
                  <p>
                    Your organization has IP access restrictions enabled. Contact your
                    administrator to add your IP address to the whitelist.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleRetry}
                disabled={checking}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {checking ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
                Try Again
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors"
              >
                Sign Out
              </button>
            </div>

            <p className="text-xs text-slate-400 mt-6">
              If you're using a VPN, try reconnecting. Otherwise, contact your administrator.
            </p>
          </div>
        </Modal.Content>
      </Modal>
    );
  }

  return <>{children}</>;
}
